import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, SearchIcon, SlidersHorizontalIcon } from 'lucide-react';
import { sections as defaultSections } from '../data';
import { FormConfig, FormData } from '../types';
import { FormSection } from '../components/FormSection';
import { LoanHeader } from '../components/LoanHeader';
import { SaveBar } from '../components/SaveBar';
import { AdminConfig } from './AdminConfig';
import { buildDefaultConfig, clearStoredConfig, computeHiddenFields, fieldCatalog, loadConfig, saveConfig } from '../utils/config';
function buildInitialValues(): FormData {
  const data: FormData = {};
  defaultSections.forEach(s => s.fields.forEach(f => data[f.id] = f.value));
  return data;
}
export function LoanForm() {
  const initial = useMemo(buildInitialValues, []);
  const [values, setValues] = useState<FormData>(initial);
  const [saved, setSaved] = useState<FormData>(initial);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState(false);
  const [config, setConfig] = useState<FormConfig>(() => loadConfig());
  const [view, setView] = useState<'record' | 'admin'>('record');
  useEffect(() => {
    saveConfig(config);
  }, [config]);
  const dirtyIds = useMemo(() => {
    const set = new Set<string>();
    Object.keys(values).forEach(id => {
      if (String(values[id]) !== String(saved[id])) set.add(id);
    });
    return set;
  }, [values, saved]);
  const hiddenFieldIds = useMemo(() => computeHiddenFields(config, values), [config, values]);
  const handleChange = (id: string, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaved(values);
      setSaving(false);
      setToast(true);
      setTimeout(() => setToast(false), 2600);
    }, 700);
  };
  const handleDiscard = () => setValues(saved);
  const handleResetConfig = () => {
    clearStoredConfig();
    setConfig(buildDefaultConfig());
  };

  // Resolve the visible sections from admin config + rules + search.
  const q = query.trim().toLowerCase();
  const resolvedSections = config.sections.filter(s => !s.hidden).map(s => {
    const fields = s.fieldIds.map(id => fieldCatalog[id]).filter(f => f && !hiddenFieldIds.has(f.id)).filter(f => q ? f.label.toLowerCase().includes(q) : true);
    const hiddenCount = s.fieldIds.filter(id => hiddenFieldIds.has(id)).length;
    return {
      ...s,
      resolvedFields: fields,
      hiddenCount
    };
  }).filter(s => q ? s.resolvedFields.length > 0 : true);
  if (view === 'admin') {
    return <AdminConfig config={config} values={values} onChange={setConfig} onExit={() => setView('record')} onReset={handleResetConfig} data-id="element-233" data-mp="tnfqikyk36d9" />;
  }
  return <div className="min-h-full w-full bg-slate-100 pb-32" data-id="element-234" data-mp="30bzi5hxkhz5u">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-8" data-id="element-235" data-mp="30aeoduj2uk91">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-236" data-mp="354iwih42wybp">
          <div data-id="element-237" data-mp="2rcunimp4mpyy">
            <p className="text-sm font-medium text-slate-400" data-id="element-238" data-mp="2rde2pvne709a">Loans</p>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-id="element-239" data-mp="2ve6aekgm8x3g">Loan Detail</h1>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto" data-id="element-240" data-mp="3j40m2yp78fm4">
            {config.showSearch && <div className="relative w-full sm:w-72" data-id="element-241" data-mp="3o1ae8m3z35dn">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-242" data-mp="3a2kesztoktm4" />
                <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search fields…" aria-label="Search fields" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-243" data-mp="3f0cier3ou465" />
              </div>}
            <button type="button" onClick={() => setView('admin')} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900" data-id="element-244" data-mp="296bz3u816qoj">
              <SlidersHorizontalIcon className="h-4 w-4" data-id="element-245" data-mp="2strsbpguxbgg" />
              Admin
            </button>
          </div>
        </div>

        <div className="space-y-5" data-id="element-246" data-mp="2nvhwrdr808ub">
          {config.showHeader && <LoanHeader values={values} metricIds={config.headerMetricIds} data-id="element-247" data-mp="1hemmwdpccyt5" />}

          {resolvedSections.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-400" data-id="element-248" data-mp="1cei6enofo42x">
              {q ? `No fields match “${query}”.` : 'All sections are hidden by the current configuration.'}
            </div> : resolvedSections.map(section => <FormSection key={section.id} title={section.title} icon={section.icon} fields={section.resolvedFields} columns={section.columns} density={config.density} defaultCollapsed={section.defaultCollapsed} hiddenCount={section.hiddenCount} values={values} dirtyIds={dirtyIds} onChange={handleChange} data-id="element-249" data-mp="wm9bwnq7x5s" />)}
        </div>
      </div>

      <SaveBar count={dirtyIds.size} saving={saving} onSave={handleSave} onDiscard={handleDiscard} data-id="element-250" data-mp="2mdt24opag00s" />

      <AnimatePresence data-id="element-251" data-mp="2qelbrj22o6xl">
        {toast && <motion.div initial={{
        opacity: 0,
        y: -12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} className="fixed left-1/2 top-5 z-50 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-xl" data-id="element-252" data-mp="2qfmwygcsy9t1">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-400" data-id="element-253" data-mp="2jqzmqlhtaa8" />
            All changes saved
          </motion.div>}
      </AnimatePresence>
    </div>;
}